import React from "react"
import { useSelector, useDispatch } from "react-redux"

import Images from "assets/images"
import {
    VIEW, EDIT, DELETE,
    PROVEN_NO, PROVEN_IN_USE, PRIOR_USE,
} from "const"
import { QidProduct } from "server/models"
import { qidProductsRoute } from "client/parts/routes"
import {
    PRM_QID_PRODUCT,
    StoreType,
    addModal,
} from "client/store"
import { hasAccess } from "client/utils"
import {
    Logo,
    Link,
    DateFormatter,
    TableColumn,
    TablePopoverData,
    TablePopover,
} from "client/components"

const Columns = (shortView: boolean) => {
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const productLink = (product: QidProduct, linkContent: any) => {
        const href = `${qidProductsRoute}/${VIEW}/${product.id}`
        return (
            <Link
                href={href}
                onClick={() => dispatch(addModal({ url: href }))}
            >
                {linkContent}
            </Link>
        )
    }

    const editAccess = hasAccess(PRM_QID_PRODUCT, EDIT)
    const deleteAccess = hasAccess(PRM_QID_PRODUCT, DELETE)

    if (shortView) {
        const shortColumns: TableColumn[] = [
            {
                field: "logo",
                header: lang.logo,
                centerAlign: true,
                formatter: (product: QidProduct) => (
                    productLink(product, <Logo table logo={product.logo || Images.qidProductLogo} />)
                ),
            },
            {
                field: "name",
                header: lang.name,
                sortable: true,
                cellMinWidth: "150px",
                cellMaxWidth: "300px",
                formatter: (product: QidProduct) => productLink(product, product.name),
            },
        ]
        return shortColumns
    }

    const columns: TableColumn[] = [
        {
            field: "logo",
            header: lang.logo,
            centerAlign: true,
            formatter: (product: QidProduct) => (
                productLink(product, <Logo table logo={product.logo || Images.qidProductLogo} />)
            ),
        },
        {
            field: "name",
            header: lang.name,
            sortable: true,
            cellMinWidth: "150px",
            cellMaxWidth: "300px",
            formatter: (product: QidProduct) => productLink(product, product.name),
        },
        {
            field: "company",
            header: lang.company,
            sortable: true,
            cellMinWidth: "150px",
            cellMaxWidth: "300px",
            formatter: (product: QidProduct) => product.company.name,
        },
        {
            field: "type",
            header: lang.type,
            sortable: true,
            cellMinWidth: "100px",
        },
        {
            field: "mode",
            header: lang.modes,
            cellMinWidth: "150px",
            formatter: (product: QidProduct) => {
                const dataList: TablePopoverData[] = []

                if (product.lowDemandMode) {
                    dataList.push({
                        id: `${product.id}lowDemandMode`,
                        title: lang.modeLowDemand,
                    })
                }
                if (product.highDemandMode) {
                    dataList.push({
                        id: `${product.id}highDemandMode`,
                        title: lang.modeHighDemand,
                    })
                }
                if (product.continuousMode) {
                    dataList.push({
                        id: `${product.id}continuousMode`,
                        title: lang.modeContinuous,
                    })
                }

                return (
                    <TablePopover
                        id={`${product.id}modeList`}
                        dataList={dataList}
                        placement="right"
                        title={lang.modes}
                        editAccess={false}
                    />
                )
            },
        },
        {
            field: "logicSolver",
            header: lang.logicSolver,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (product: QidProduct) => (product.logicSolver ? lang.yes : lang.no),
        },
        {
            field: "provenInUse",
            header: lang.provenReliabilityData,
            sortable: true,
            cellMinWidth: "200px",
            formatter: (product: QidProduct) => {
                if (product.provenInUse === PROVEN_NO) return lang.no
                if (product.provenInUse === PROVEN_IN_USE) return lang.provenInUse
                if (product.provenInUse === PRIOR_USE) return lang.priorUse
                return null
            },
        },
    ]

    if (editAccess) { // for Super Admin
        columns.unshift({
            field: "id",
            header: lang.id,
            sortable: true,
        })
        columns.push(
            {
                field: "status",
                header: lang.status,
                centerAlign: true,
                sortable: true,
                editAccess: () => editAccess,
            },
            {
                field: "createdAt",
                header: lang.created,
                sortable: true,
                formatter: (product: QidProduct) => <DateFormatter date={product.createdAt} />,
            },
        )
    }

    if (deleteAccess) {
        columns.push({
            field: "delete",
            centerAlign: true,
            deleteAccess: () => true,
        })
    }

    return columns
}
export default Columns
